import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import type { CanjeRow, JornadaCanje } from '../../api/canjes';
import {
  ESTADO_TURNO_CLS, ESTADO_TURNO_LABEL, canjesVigentes, esPendiente, estadoDeTurno,
  type CanjesPorTurno as Agrupado, type EstadoTurnoCanje,
} from '../../domain/canjesPorTurno';
import { BotonDetalle } from '../../components/DetalleExpandible';
import TarjetasResumen from '../../components/TarjetasResumen';

const TZ = 'America/Lima';

/**
 * La bandeja de canjes vista POR TURNO (0375): una fila por jornada programada, tenga canjes o no.
 *
 * Es el modelo de «Visitas a puestos»: los turnos en cero se ven, y cada uno trae su «+ Cargar canje» con la campaña,
 * la fecha, la tienda y la persona ya puestas. Las acciones sobre cada canje (corregir, anular, borrar, historial)
 * las pone la bandeja con `renderCanje`, para que sean las mismas de la lista por canje y no una segunda copia.
 */
export default function CanjesPorTurno({
  jornadas, agrupado, hoy, soloPendientes, mostrarCampania, nombreCampania, puedeCargarEn, onCargar, renderCanje,
}: {
  jornadas: JornadaCanje[];
  agrupado: Agrupado;
  /** Hoy en Lima, `yyyy-MM-dd`. */
  hoy: string;
  soloPendientes: boolean;
  /** Con «Todas las campañas» cada fila dice de cuál es. */
  mostrarCampania: boolean;
  nombreCampania: (id: string) => string;
  puedeCargarEn: (campaignId: string) => boolean;
  onCargar: (j: JornadaCanje) => void;
  renderCanje: (c: CanjeRow) => ReactNode;
}) {
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());

  /** El estado se calcula UNA vez: lo usan la fila, el filtro y las tarjetas, y no pueden decir cosas distintas. */
  const conEstado = useMemo(() => jornadas.map((j) => {
    const canjes = agrupado.porTurno.get(j.positionId) ?? [];
    const vigentes = canjesVigentes(canjes);
    return { j, canjes, vigentes, estado: estadoDeTurno(j, vigentes, hoy) };
  }), [jornadas, agrupado, hoy]);

  const visibles = soloPendientes ? conEstado.filter((x) => esPendiente(x.estado)) : conEstado;
  const cuenta = (e: EstadoTurnoCanje) => conEstado.filter((x) => x.estado === e).length;
  const sueltosVigentes = canjesVigentes(agrupado.sueltos);

  function alternar(id: string) {
    setAbiertos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  if (jornadas.length === 0) {
    return (
      <p className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
        No hay turnos programados con canje en esas fechas.
        {agrupado.sueltos.length > 0 && ` Hay ${agrupado.sueltos.length} canje(s) registrados sin turno: se ven en «Por canje».`}
      </p>
    );
  }

  return (
    <div>
      <TarjetasResumen indicadores={[
        { titulo: 'Turnos', valor: jornadas.length, detalle: 'tienda, día y plaza programados' },
        { titulo: 'Con canjes', valor: cuenta('con_canjes'), tono: 'bueno' },
        { titulo: 'Sin canjes', valor: cuenta('sin_canjes'), tono: 'atencion', detalle: 'ya pasaron, con persona' },
        { titulo: 'Sin persona', valor: cuenta('sin_persona'), tono: 'malo', detalle: 'nadie aprobado en el turno' },
        { titulo: 'Canjes', valor: canjesVigentes(conEstado.flatMap((x) => x.canjes)) },
        { titulo: 'Fuera de turno', valor: sueltosVigentes,
          detalle: 'canjes sin turno en la lista; se ven en «Por canje»' },
      ]} />

      {visibles.length === 0 ? (
        <p className="text-sm text-gray-700">No hay turnos pendientes con esos filtros.</p>
      ) : (
        <div className="overflow-x-auto rounded border">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Tienda</th>
                <th className="px-3 py-2">Persona</th>
                <th className="px-3 py-2">Ingreso</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2 text-right">Canjes</th>
                <th className="px-3 py-2"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {visibles.map(({ j, canjes, vigentes, estado }) => {
                const abierto = abiertos.has(j.positionId);
                const cargable = j.asignacion !== null && j.fecha <= hoy && puedeCargarEn(j.campaignId);
                return (
                  <Fragment key={j.positionId}>
                    <tr>
                      <td className="whitespace-nowrap px-3 py-2 align-top">
                        {j.fecha}
                        <div className="text-xs text-gray-500">{j.inicio} a {j.fin}</div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        {j.pdv.name}
                        {mostrarCampania && (
                          <div className="text-xs text-gray-500">{nombreCampania(j.campaignId)}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {j.asignacion === null ? <span className="text-gray-500">Sin persona</span> : (
                          <>
                            {j.asignacion.nombre}
                            <div className="text-xs text-gray-500">{j.asignacion.dni}</div>
                          </>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 align-top">
                        {j.ingreso === null
                          ? <span className="text-gray-500">{j.asignacion === null ? '—' : 'no marcó'}</span>
                          : formatInTimeZone(new Date(j.ingreso), TZ, 'HH:mm')}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className={`rounded px-1.5 py-0.5 text-xs ${ESTADO_TURNO_CLS[estado]}`}>
                          {ESTADO_TURNO_LABEL[estado]}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right align-top font-medium">{vigentes}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right align-top">
                        <div className="flex flex-col items-end gap-1">
                          {cargable && (
                            <button type="button" onClick={() => onCargar(j)}
                              className="rounded bg-blue-600 px-2.5 py-1 text-xs text-white">
                              + Cargar canje
                            </button>
                          )}
                          {/* Se dice por qué no hay botón, en vez de dejar que parezca un error. */}
                          {j.asignacion === null && j.fecha <= hoy && puedeCargarEn(j.campaignId) && (
                            <span className="max-w-[14rem] whitespace-normal text-xs text-gray-600">
                              Asigna primero a la persona en Asignaciones.
                            </span>
                          )}
                          {canjes.length > 0 && (
                            <BotonDetalle abierto={abierto} onClick={() => alternar(j.positionId)} />
                          )}
                        </div>
                      </td>
                    </tr>
                    {abierto && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-3 py-2">
                          <div className="space-y-2">{canjes.map((c) => <Fragment key={c.id}>{renderCanje(c)}</Fragment>)}</div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
